import React, { useRef, useCallback, useState } from 'react';
import {
  Box,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  FilterAlt as FilterIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';

/**
 * Standard button styles used across PAM applications
 */
const BUTTON_STYLES = {
  upload: {
    backgroundColor: 'lightblue',
    color: 'black',
    '&:hover': { backgroundColor: 'rgba(173, 216, 230, 0.8)' },
  },
  template: {
    backgroundColor: 'lightcyan',
    color: 'black',
    '&:hover': { backgroundColor: 'rgba(224, 255, 255, 0.8)' },
  },
  export: {
    backgroundColor: 'lightgreen',
    color: 'black',
    '&:hover': { backgroundColor: 'rgba(144, 238, 144, 0.8)' },
  },
  addRow: {
    backgroundColor: 'lightgrey',
    color: 'black',
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
  },
  save: {
    backgroundColor: 'lightgrey',
    color: 'black',
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
  },
  saveActive: {
    backgroundColor: 'orange',
    color: 'black',
    '&:hover': { backgroundColor: 'rgba(255, 165, 0, 0.8)' },
  },
  clearFilters: {
    backgroundColor: 'lavender',
    color: 'black',
    '&:hover': { backgroundColor: 'rgba(230, 230, 250, 0.8)' },
  },
  delete: {
    backgroundColor: '#ffcdd2',
    color: 'black',
    '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.3)' },
  },
  deleteActive: {
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.8)' },
  },
  disabled: {
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
};

/**
 * GridToolbar - Configurable toolbar for PAM grids
 *
 * @param {Object} props
 * @param {string} props.title - Grid title
 *
 * File operations:
 * @param {boolean} [props.showUpload=false] - Show upload button
 * @param {Function} [props.onUpload] - Upload file handler (file) => void
 * @param {string} [props.uploadLabel='Upload'] - Upload button label
 * @param {string} [props.uploadTooltip] - Upload button tooltip
 * @param {string} [props.acceptedFileTypes='.csv,.xlsx,.xls'] - Accepted file types
 * @param {Array<Object>} [props.uploadOptions] - Upload type options [{id, label, icon?}] - shows dropdown when length > 1
 * @param {Function} [props.onUploadWithType] - Upload handler with type: (file, typeId) => void
 *
 * @param {boolean} [props.showTemplate=false] - Show template download button
 * @param {Function} [props.onDownloadTemplate] - Template download handler
 * @param {string} [props.templateLabel='Template'] - Template button label
 *
 * @param {boolean} [props.showExport=true] - Show export button
 * @param {Function} [props.onExport] - Export handler (default export)
 * @param {string} [props.exportLabel='Export Excel'] - Export button label
 * @param {Array<Object>} [props.exportFormats] - Export format options [{id, label, icon?}] - shows dropdown when length > 1
 * @param {Function} [props.onExportFormat] - Export format handler: (formatId) => void
 *
 * Row operations:
 * @param {boolean} [props.showAddRow=false] - Show add row button
 * @param {Function} [props.onAddRow] - Add row handler
 * @param {string} [props.addRowLabel='Add Row'] - Add row button label
 *
 * @param {boolean} [props.showSave=false] - Show save button
 * @param {Function} [props.onSave] - Save handler
 * @param {number} [props.changedRowCount=0] - Number of changed rows
 * @param {boolean} [props.isSaving=false] - Whether save is in progress
 *
 * @param {boolean} [props.showClearFilters=true] - Show clear filters button
 * @param {Function} [props.onClearFilters] - Clear filters handler
 *
 * Bulk operations:
 * @param {boolean} [props.showBulkDelete=false] - Show bulk delete button
 * @param {Function} [props.onBulkDelete] - Bulk delete handler
 * @param {number} [props.selectedRowCount=0] - Number of selected rows
 *
 * Date selection:
 * @param {boolean} [props.showDateSelect=false] - Show date selector
 * @param {Array<string>} [props.availableDates=[]] - Available dates
 * @param {string} [props.selectedDate=''] - Currently selected date
 * @param {Function} [props.onDateChange] - Date change handler
 * @param {boolean} [props.isLoadingDates=false] - Whether dates are loading
 *
 * State:
 * @param {boolean} [props.isLoading=false] - Whether grid is loading
 * @param {Object} [props.customButtons=[]] - Additional custom buttons
 */
export const GridToolbar = ({
  // Title
  title,
  subtitle,

  // File operations
  showUpload = false,
  onUpload,
  uploadLabel = 'Upload',
  uploadTooltip = 'Upload Excel (.xlsx, .xls) or CSV file',
  acceptedFileTypes = '.csv,.xlsx,.xls',
  uploadOptions = [],
  onUploadWithType,

  showTemplate = false,
  onDownloadTemplate,
  templateLabel = 'Template',

  showExport = true,
  onExport,
  exportLabel = 'Export Excel',
  exportFormats = [],
  onExportFormat,

  // Row operations
  showAddRow = false,
  onAddRow,
  addRowLabel = 'Add Row',

  showSave = false,
  onSave,
  changedRowCount = 0,
  isSaving = false,

  showClearFilters = true,
  onClearFilters,

  // Bulk operations
  showBulkDelete = false,
  onBulkDelete,
  selectedRowCount = 0,

  // Date selection
  showDateSelect = false,
  availableDates = [],
  selectedDate = '',
  onDateChange,
  isLoadingDates = false,

  // State
  isLoading = false,

  // Custom buttons
  customButtons = [],

  // Custom rendered React nodes (rendered after customButtons, before Save).
  // Use this when a "button" needs to manage its own state (dropdowns,
  // dialogs, etc.) — `customButtons` only accepts {label, onClick, …}
  // shapes, which can't express a stateful component like GridConfigMenu.
  customComponents = [],

  // Container styling
  sx = {},
}) => {
  const fileInputRef = useRef(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null);
  const [selectedUploadType, setSelectedUploadType] = useState(null);

  const hasChanges = changedRowCount > 0;
  const hasSelection = selectedRowCount > 0;
  const hasMultipleExportFormats = exportFormats.length > 1;
  const hasMultipleUploadOptions = uploadOptions.length > 1;

  // Handle file selection
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      if (selectedUploadType && onUploadWithType) {
        onUploadWithType(file, selectedUploadType);
      } else if (onUpload) {
        onUpload(file);
      }
    }
    // Reset input so same file can be selected again
    event.target.value = '';
    setSelectedUploadType(null);
  }, [onUpload, onUploadWithType, selectedUploadType]);

  // Handle upload button click (simple or dropdown)
  const handleUploadClick = useCallback((event) => {
    if (hasMultipleUploadOptions) {
      setUploadMenuAnchor(event.currentTarget);
    } else {
      fileInputRef.current?.click();
    }
  }, [hasMultipleUploadOptions]);

  // Handle upload type selection from dropdown
  const handleUploadTypeSelect = useCallback((typeId) => {
    setSelectedUploadType(typeId);
    setUploadMenuAnchor(null);
    fileInputRef.current?.click();
  }, []);

  // Handle export button click (simple or dropdown)
  const handleExportClick = useCallback((event) => {
    if (hasMultipleExportFormats) {
      setExportMenuAnchor(event.currentTarget);
    } else if (onExport) {
      onExport();
    } else if (exportFormats.length === 1 && onExportFormat) {
      onExportFormat(exportFormats[0].id);
    }
  }, [hasMultipleExportFormats, onExport, exportFormats, onExportFormat]);

  // Handle export format selection from dropdown
  const handleExportFormatSelect = useCallback((formatId) => {
    setExportMenuAnchor(null);
    onExportFormat?.(formatId);
  }, [onExportFormat]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1,
        ...sx,
      }}
    >
      {/* Left side - Title and Date Select */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {(title || subtitle) && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {title && (
              <Typography variant="h5" component="h2">
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        {showDateSelect && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Select Date</InputLabel>
              <Select
                value={selectedDate}
                onChange={(e) => onDateChange?.(e.target.value)}
                disabled={isLoadingDates || isLoading}
                label="Select Date"
              >
                {availableDates.map((date) => (
                  <MenuItem key={date} value={date}>
                    {date}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isLoadingDates && <CircularProgress size={20} />}
          </Box>
        )}
      </Box>

      {/* Right side - Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {/* Upload Button (with optional dropdown) */}
        {showUpload && (
          <>
            <Tooltip title={uploadTooltip}>
              <span>
                <Button
                  sx={BUTTON_STYLES.upload}
                  startIcon={<UploadIcon />}
                  endIcon={hasMultipleUploadOptions ? <ArrowDropDownIcon /> : undefined}
                  onClick={handleUploadClick}
                  disabled={isLoading}
                >
                  {uploadLabel}
                </Button>
              </span>
            </Tooltip>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept={acceptedFileTypes}
              onChange={handleFileSelect}
            />

            {/* Upload Type Menu */}
            {hasMultipleUploadOptions && (
              <Menu
                anchorEl={uploadMenuAnchor}
                open={Boolean(uploadMenuAnchor)}
                onClose={() => setUploadMenuAnchor(null)}
              >
                {uploadOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    onClick={() => handleUploadTypeSelect(option.id)}
                  >
                    {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                    <ListItemText>{option.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </>
        )}

        {/* Template Button */}
        {showTemplate && (
          <Tooltip title="Download template file with correct column headers">
            <span>
              <Button
                sx={BUTTON_STYLES.template}
                startIcon={<DownloadIcon />}
                onClick={onDownloadTemplate}
                disabled={isLoading}
              >
                {templateLabel}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Export Button (with optional dropdown) */}
        {showExport && (
          <>
            <Tooltip title="Export data to file">
              <span>
                <Button
                  sx={BUTTON_STYLES.export}
                  startIcon={<DownloadIcon />}
                  endIcon={hasMultipleExportFormats ? <ArrowDropDownIcon /> : undefined}
                  onClick={handleExportClick}
                  disabled={isLoading}
                >
                  {exportLabel}
                </Button>
              </span>
            </Tooltip>

            {/* Export Format Menu */}
            {hasMultipleExportFormats && (
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
              >
                {exportFormats.map((format) => (
                  <MenuItem
                    key={format.id}
                    onClick={() => handleExportFormatSelect(format.id)}
                  >
                    {format.icon && <ListItemIcon>{format.icon}</ListItemIcon>}
                    <ListItemText>{format.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </>
        )}

        {/* Add Row Button */}
        {showAddRow && (
          <Tooltip title="Add a new blank row">
            <span>
              <Button
                sx={BUTTON_STYLES.addRow}
                startIcon={<AddIcon />}
                onClick={onAddRow}
                disabled={isLoading}
              >
                {addRowLabel}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Bulk Delete Button */}
        {showBulkDelete && (
          <Tooltip title={hasSelection ? `Delete ${selectedRowCount} selected rows` : 'Select rows to delete'}>
            <span>
              <Button
                sx={hasSelection ? BUTTON_STYLES.deleteActive : BUTTON_STYLES.delete}
                startIcon={<DeleteIcon />}
                onClick={onBulkDelete}
                disabled={isLoading || !hasSelection}
              >
                {hasSelection ? `Delete (${selectedRowCount})` : 'Delete Selected'}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Custom Buttons */}
        {customButtons.map((btn, index) => (
          <Tooltip key={index} title={btn.tooltip || ''}>
            <span>
              <Button
                sx={btn.sx || BUTTON_STYLES.addRow}
                startIcon={btn.icon}
                onClick={btn.onClick}
                disabled={btn.disabled || isLoading}
              >
                {btn.label}
              </Button>
            </span>
          </Tooltip>
        ))}

        {/* Custom rendered components (slot — caller controls their state) */}
        {customComponents.map((node, index) => (
          <React.Fragment key={`cc-${index}`}>{node}</React.Fragment>
        ))}

        {/* Save Button */}
        {showSave && (
          <Tooltip title={hasChanges ? `Save ${changedRowCount} changed rows` : 'No changes to save'}>
            <span>
              <Button
                sx={{
                  ...(hasChanges ? BUTTON_STYLES.saveActive : BUTTON_STYLES.save),
                  '&:disabled': BUTTON_STYLES.disabled,
                }}
                startIcon={<SaveIcon />}
                onClick={onSave}
                disabled={isLoading || isSaving || !hasChanges}
              >
                {isSaving ? 'Saving...' : `Save${hasChanges ? ` (${changedRowCount})` : ''}`}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Clear Filters Button */}
        {showClearFilters && (
          <Tooltip title="Clear all column filters and quick filter">
            <span>
              <Button
                sx={BUTTON_STYLES.clearFilters}
                startIcon={<FilterIcon />}
                onClick={onClearFilters}
                disabled={isLoading}
              >
                Clear Filters
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// Export button styles for external use
export { BUTTON_STYLES };

export default GridToolbar;
